import { useMemo } from 'react'
import { useGuildMembers } from 'app/frontend/base/config/queries'
import { DataTable, EmptyMessageWithLoader } from '@/components/ui/data-table'
import { ColumnDef } from '@tanstack/react-table'
import {
  characterClass,
  name,
  realm,
  level,
  itemLevel,
  refreshedAt,
  detailedRankInGuild,
} from 'app/frontend/base/config/column-definitions/character'
import { useTranslation } from 'react-i18next'
import { useGuild } from 'app/frontend/base/config/context/guild'
import Section from 'app/frontend/base/components/Section'
import useBasePath from 'app/frontend/base/hooks/useBasePath'
import { useCharacterDetailsStore } from 'app/frontend/base/state/useCharacterDetailsStore'
import GuildDataRefreshButton from 'app/frontend/base/components/GuildDataRefreshButton'

const GuildMembers = () => {
  const baseGuildPath = useBasePath('guild')
  const { guild, guildReference } = useGuild()
  const { t } = useTranslation()
  const { detailsStore, fetchNewDetails } =
    useCharacterDetailsStore<wowaudit.CharacterReference>(
      (character) => character.id,
    )

  const {
    data: members,
    isPending,
    isLoadingError,
    error,
    refetch,
  } = useGuildMembers(guildReference?.id)

  const columns: ColumnDef<wowaudit.CharacterReference>[] = useMemo(
    () => [
      name,
      characterClass,
      realm,
      level,
      itemLevel(detailsStore, (character) => character.id),
      detailedRankInGuild(guild?.guildRanks),
      refreshedAt(t, 'guild'),
    ],
    [detailsStore, guild?.guildRanks, t],
  )

  return (
    <Section title={`Members | ${guild?.name}`}>
      <h2 className='flex justify-between'>
        <span>
          Guild members {guild?.guildMembers ? `(${guild.guildMembers})` : ''}
        </span>

        <GuildDataRefreshButton
          successKey='dashboard.guild.guild-update-success'
          callback={refetch}
          queryToInvalidate={['guild', { path: baseGuildPath }]}
        />
      </h2>

      <DataTable
        columns={columns}
        data={members || []}
        includeHeader={true}
        pagination={50}
        onVisibleRowsChange={fetchNewDetails}
        filterType='includesString'
        sortingState={[{ id: 'rankInGuild', desc: false }]}
        emptyMessage={() => (
          <EmptyMessageWithLoader
            emptyMessageKey='dashboard.guild.no-members'
            isPending={isPending}
            isLoadingError={isLoadingError}
            error={error}
            t={t}
          />
        )}
      />
    </Section>
  )
}

export default GuildMembers
